import { ReactNode } from 'react';
import { Box, Flex, Spinner, SystemProps } from '@storyofams/react-ui';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import { Button, Heading, ProductItem } from '~components';
import { useShopify } from '~context';

import { ProductCardVariants } from '../product/ProductCardVariants';
import { ProductCard } from '../product/ProductCard';

import { ProductCompactCard } from '../product/ProductCompactCard';

const messages = defineMessages({
  somethingWentWrong: 'Something went wrong',
  noResults: 'There are no results',
  resetFilters: 'Remove filters',
  add: 'Add',
});

interface ProductsGridProps {
  products: { edges: Record<string, any>[] };
  error?: any;
  isLoading?: boolean;
  hasFilters?: boolean;
  resetFilters?: () => void;
  withControls?: boolean;
  skipPortion?: boolean;
  hasCompactCards?: boolean;
}

export const Grid = ({
  children,
  ...props
}: SystemProps & { children: ReactNode }) => (
  <Box
    display="grid"
    width="100%"
    gridRowGap={[3, 5]}
    gridColumnGap={[2, 3]}
    gridTemplateColumns={['repeat(1, 1fr)', 'repeat(2, 1fr)', 'repeat(3, 1fr)']}
    {...props}
  >
    {children}
  </Box>
);

export const ProductsGrid = ({
  products,
  error,
  isLoading,
  hasFilters,
  resetFilters,
  withControls,
  skipPortion,
  hasCompactCards,
  ...props
}: ProductsGridProps & SystemProps) => {
  const { portion } = useShopify();
  const intl = useIntl();

  if (isLoading) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        flex={1}
        minHeight={['200px', '300px']}
      >
        <Spinner color="grey500" />
      </Flex>
    );
  } else if (error) {
    return (
      <Flex
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        flex={1}
        minHeight={['200px', '300px']}
      >
        <Heading
          variant="h5"
          as="h3"
          textAlign="center"
          lineHeight="150%"
          pb={[3, 6]}
          px={2}
          mt={[0, '-16px']}
        >
          {intl.formatMessage(messages.somethingWentWrong)}
        </Heading>
      </Flex>
    );
  } else if (products?.edges?.length <= 0) {
    return (
      <Flex
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        flex={1}
        minHeight={['200px', '300px']}
      >
        <Heading
          variant="h5"
          as="h3"
          textAlign="center"
          lineHeight="150%"
          px={2}
          mt={[0, '-16px']}
        >
          {intl.formatMessage(messages.noResults)}
        </Heading>

        {hasFilters && (
          <Button mt={3} onClick={resetFilters}>
            {intl.formatMessage(messages.resetFilters)}
          </Button>
        )}
      </Flex>
    );
  }

  return (
    <Grid {...props}>
      {products?.edges
        ?.filter(({ node }) =>
          skipPortion
            ? true
            : node?.tags?.includes(`portion_${portion || 'normal'}`),
        )
        .map(({ node }, i) => {
          // background repeating pattern
          const bg = [
            !((i + 1) % 6)
              ? 'primary100'
              : i + 1 === 2 || !((i + 1) % 8)
              ? 'secondary'
              : 'white',
            i + 1 === 2 || (i + 1) % 7 === 0
              ? 'secondary'
              : i + 1 === 6 || (i + 1) % 11 === 0
              ? 'primary100'
              : 'white',
          ];
          const isThemeBox = node?.tags?.includes('is_box');

          return hasCompactCards ? (
            <ProductCompactCard
              key={node?.id}
              product={node}
              bg={bg}
              index={i}
            />
          ) : isThemeBox ? (
              <ProductCard
                key={node?.id}
                product={node}
                withControls={withControls}
                bg={bg}
              />
            ) : (
              <ProductItem
                key={node?.id}
                product={node}
                textureType={1}
                buttonText={<FormattedMessage {...messages.add} />}
              />
              // <ProductCardVariants
              //   key={node?.id}
              //   product={node}
              //   withControls={withControls}
              //   bg={bg}
              // />
            )
        })}
    </Grid>
  );
};
